import React, { lazy, Suspense } from "react";
import { withLazyRoute } from "@/components/loading/LazyRouteWrapper";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import {
  AuthRoute,
  SuperUserRoute,
  FreelancerRoute,
  ProtectedRoute,
  StakeholderRoute,
  StudentRoute,
} from "./routes";
import {
  stakeholderPages,
  superuserPages,
  getFreelancerPages,
  studentPages,
} from "router/pages";
import { AuthLayout, OnboardingLayout, CoreLayout } from "./layouts";
import {
  Login,
  SignUp,
  ForgotPassword,
  ResetPassword,
  LoginMFACode,
  SessionExpired,
  NotFound,
  AccountDeactivated,
  SignOut,
} from "views/Auth";

// import { FreelancerOnboarding } from "@/views/Onboarding";
// import {
//   StakeholderOnboarding,
//   NewProjectSetup,
//   ProjectIdeaChat,
// } from "views/Stakeholder";
// import { StudentOnboarding, StudentManageSubscription } from "views/Student";
// import {
//   AccountSettings,
//   NotificationCenter,
//   NeedsResetPassword,
// } from "views/Shared";

const FreelancerOnboarding = withLazyRoute(
  lazy(() => import("views/Onboarding/FreelancerOnboarding")),
  "Loading freelancer onboarding..."
);
const StakeholderOnboarding = withLazyRoute(
  lazy(() => import("views/Onboarding/StakeholderOnboarding")),
  "Loading stakeholder onboarding..."
);
const StudentOnboarding = withLazyRoute(
  lazy(() => import("views/Onboarding/StudentOnboarding")),
  "Loading student onboarding..."
);
const NeedsResetPassword = withLazyRoute(
  lazy(() =>
    import("views/Shared/NeedsResetPassword/NeedsResetPassword").then((m) => ({
      default: m.NeedsResetPassword,
    }))
  ),
  "Loading needs reset password..."
);
const AccountSettings = withLazyRoute(
  lazy(() =>
    import("views/Shared/AccountSettings/AccountSettings").then((m) => ({
      default: m.AccountSettings,
    }))
  ),
  "Loading account settings..."
);
const NotificationCenter = withLazyRoute(
  lazy(() =>
    import("views/Shared/Notifications/NotificationCenter").then((m) => ({
      default: m.NotificationCenter,
    }))
  ),
  "Loading notification center..."
);
const NewProjectSetup = withLazyRoute(
  lazy(() =>
    import("views/Stakeholder/StakeholderProjectHub/NewProjectSetup").then(
      (m) => ({ default: m.NewProjectSetup })
    )
  ),
  "Loading new project setup..."
);

const StudentManageSubscription = withLazyRoute(
  lazy(() =>
    import("views/Student/ManageSubscription/StudentManageSubscription").then(
      (m) => ({ default: m.StudentManageSubscription })
    )
  ),
  "Loading subscription management..."
);

import StandardErrorBoundary from "@/components/layout/StandardErrorBoundary";
import { useAuth } from "hooks/auth";
import { mapPagesToReactRouter } from "router/mapPagesToReactRouter";
import { LazyLoadingSpinner } from "@/components/loading/LazyLoadingSpinner";
import { useEffect, useState } from "react";
import OAuthCallback from "components/auth/OAuthCallback";

export const AppRoutes = () => {
  const { isLoading, userInfo, role } = useAuth();
  const [chunksPreloaded, setChunksPreloaded] = useState(false);

  // useEffect(() => {
  //   if (!role || chunksPreloaded) return;

  //   const preloadChunks = async () => {
  //     try {
  //       if (role === "stakeholder") {
  //         await import("views/Stakeholder");
  //       } else if (role === "freelancer") {
  //         await import("views/Freelancer");
  //       } else if (role === "student") {
  //         await import("views/Student");
  //       }
  //       await import("views/Shared");
  //       await import("views/Dashboards");
  //       setChunksPreloaded(true);
  //     } catch (error) {
  //       console.warn("Failed to preload chunks:", error);
  //       setChunksPreloaded(true);
  //     }
  //   };
  //   setIsLoading(true);
  //   preloadChunks();
  //   setIsLoading(false);
  // }, [userInfo?.role, chunksPreloaded]);

  if (isLoading) {
    return null;
  }

  const AuthRoutes = () => {
    return (
      <>
        <Route
          path="/login"
          element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          }
        />

        <Route
          path="/signup"
          element={
            <AuthRoute>
              <SignUp />
            </AuthRoute>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <AuthRoute>
              <ForgotPassword />
            </AuthRoute>
          }
        />

        <Route
          path="/reset-password"
          element={
            <AuthRoute>
              <ResetPassword />
            </AuthRoute>
          }
        />

        <Route
          path="/login-mfa-code"
          element={
            <AuthRoute>
              <LoginMFACode />
            </AuthRoute>
          }
        />

        <Route path="/session-expired" element={<SessionExpired />} />

        <Route path="/sign-out" element={<SignOut />} />

        <Route path="/oauth/callback" element={<OAuthCallback />} />
      </>
    );
  };

  const RedirectRoutes = () => {
    return (
      <>
        <Route
          path="/"
          element={<Navigate to={`/${role || "login"}`} replace />}
        />

        <Route path="*" element={<NotFound />} />

        <Route path="/account-deactivated" element={<AccountDeactivated />} />
      </>
    );
  };

  const FreelancerRoutes = () => {
    return <>{mapPagesToReactRouter(getFreelancerPages(true, userInfo), FreelancerRoute)}</>;
  };

  const SuperUserRoutes = () => {
    return <>{mapPagesToReactRouter(superuserPages, SuperUserRoute)}</>;
  };

  const StakeholderRoutes = () => {
    return <>{mapPagesToReactRouter(stakeholderPages, StakeholderRoute)}</>;
  };

  const StudentRoutes = () => {
    return <>{mapPagesToReactRouter(studentPages, StudentRoute)}</>;
  };

  return (
    // Future is used to enable startTransition for react-router-dom v7
    <Router future={{ v7_startTransition: true }}>
      <Routes>
        <Route element={<AuthLayout />}>
          {AuthRoutes()}
          {RedirectRoutes()}
        </Route>

        {/* Onboarding Layout */}
        <Route element={<OnboardingLayout />}>
          <Route
            path="/freelancer-onboarding"
            element={<FreelancerOnboarding />}
          />
          <Route
            path="/stakeholder-onboarding"
            element={<StakeholderOnboarding />}
          />
          <Route path="/student-onboarding" element={<StudentOnboarding />} />

          <Route
            path="/needs-reset-password"
            element={<NeedsResetPassword />}
          />

          <Route
            path="/executiveAssistant-onboarding"
            element={<div>Executive Assistant Onboarding</div>}
          />
        </Route>

        <Route element={<CoreLayout />}>
          <Route
            element={
              <StandardErrorBoundary>
                <Outlet />
              </StandardErrorBoundary>
            }
          >
            <Route
              path="/account-settings"
              element={
                <ProtectedRoute>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationCenter />
                </ProtectedRoute>
              }
            />
            {FreelancerRoutes()}
            {SuperUserRoutes()}
            {StakeholderRoutes()}
            {StudentRoutes()}
            {/* Additional Stakeholder Routes */}
            <Route
              path="/stakeholder/projects/new"
              element={
                <StakeholderRoute>
                  <NewProjectSetup />
                </StakeholderRoute>
              }
            />
            <Route
              path="/student/manage-subscription"
              element={
                <StudentRoute>
                  <StudentManageSubscription />
                </StudentRoute>
              }
            />
          </Route>
        </Route>
      </Routes>
    </Router>
  );
};
